import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Alert, Breadcrumb, Table, Button } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { chartApi } from '../services/api';
import { Chart } from '../types';
import ChartCard from '../components/ChartCard';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/autoplay';
import { Autoplay } from 'swiper/modules';

const CategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const [charts, setCharts] = useState<Chart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryInfo, setCategoryInfo] = useState<any>(null);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  // 카테고리 목록 접기/펼치기 상태
  const [showAllCategories, setShowAllCategories] = useState(false);
  const visibleCategories = showAllCategories ? allCategories : allCategories.slice(0, 8);

  useEffect(() => {
    const fetchCategoryCharts = async () => {
      if (!category) return;
      try {
        setLoading(true);
        const [chartsData, categoriesData] = await Promise.all([
          chartApi.getChartsByCategory(category),
          chartApi.getAllCategories()
        ]);
        setCharts(chartsData ?? []);
        setAllCategories(categoriesData);
        const found = categoriesData.find((cat: any) => cat.name === category);
        setCategoryInfo(found);
      } catch (err) {
        setError('카테고리 데이터를 불러오는데 실패했습니다.');
        console.error('Error fetching category charts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategoryCharts();
  }, [category]);

  if (loading) {
    return (
      <div className="min-vh-100" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <Container className="text-center py-5">
          <div className="d-flex flex-column align-items-center">
            <Spinner animation="border" role="status" className="mb-3">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="text-muted">{category} 카테고리 데이터를 불러오는 중...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <Container className="py-5">
          <Alert variant="danger" className="theme-card">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <Container className="py-5">
        {/* 브레드크럼 */}
        <Breadcrumb className="mb-4">
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
            <i className="fas fa-home me-1"></i>
            홈
          </Breadcrumb.Item>
          <Breadcrumb.Item active>{category}</Breadcrumb.Item>
        </Breadcrumb>

        {/* 카테고리 헤더 */}
        <Row className="mb-5">
          <Col className="text-center">
            <div className="mb-4">
              <div 
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{ 
                  width: '80px', 
                  height: '80px',
                  background: categoryInfo?.color || '#eee'
                }}
              >
                <span style={{ fontSize: '2.5rem' }}>{categoryInfo?.icon || '🏷️'}</span>
              </div>
              <h1 className="display-5 fw-bold mb-3">
                <span>
                  {category} 순위
                </span>
              </h1>
              <p className="lead text-muted mb-4">{categoryInfo?.description || ''}</p>
              <span
                style={{
                  display: 'inline-block',
                  padding: '0.35em 0.65em',
                  fontSize: '1rem',
                  fontWeight: 600,
                  lineHeight: 1,
                  color: 'white',
                  backgroundColor: categoryInfo?.color || '#6b7280',
                  borderRadius: '999px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  marginRight: '0.5em',
                  marginBottom: '0.25em'
                }}
              >
                <i className="fas fa-chart-bar me-2"></i>
                {charts.length}개의 차트
              </span>
            </div>
          </Col>
        </Row>

        {/* 차트 목록 */}
        {charts.length > 0 ? (
          <Row>
            {charts.map((chart) => (
              <Col key={chart.id} lg={4} md={6} className="mb-4">
                <ChartCard chart={chart} categories={allCategories} />
              </Col>
            ))}
          </Row>
        ) : (
          <Row>
            <Col className="text-center">
              <div className="py-5">
                <i className="fas fa-chart-bar fs-1 text-muted mb-3 d-block"></i>
                <h4 className="text-muted mb-2">아직 차트가 없습니다</h4>
                <p className="text-muted mb-4">{category} 카테고리의 첫 번째 차트를 기다려주세요!</p>
                <Link to="/" className="btn btn-primary rounded-pill px-4">
                  <i className="fas fa-arrow-left me-2"></i>
                  홈으로 돌아가기
                </Link>
              </div>
            </Col>
          </Row>
        )}

        {/* 다른 카테고리 추천 */}
        {charts.length > 0 && (
          <Row className="mt-5">
            <Col>
              <div className="text-center">
                <h4 className="mb-4">다양한 카테고리에서 탐색해보세요</h4>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                  <Swiper
                    modules={[Autoplay]}
                    spaceBetween={4}
                    slidesPerView={10}
                    loop={true}
                    autoplay={{ delay: 500, disableOnInteraction: false, pauseOnMouseEnter: true }}
                    speed={1000}
                    breakpoints={{
                      320: { slidesPerView: 4 },
                      480: { slidesPerView: 5 },
                      768: { slidesPerView: 6 },
                      1024: { slidesPerView: 10 }
                    }}
                    style={{ padding: '8px 0' }}
                  >
                    {allCategories.map((cat) => (
                      <SwiperSlide key={cat.id}>
                        <div
                          onClick={() => window.location.href = `/category/${cat.name}`}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'transform 0.18s',
                            borderRadius: '50%',
                            width: 80,
                            height: 80,
                            background: 'none',
                            padding: 0
                          }}
                          className="category-slide-icon"
                        >
                          <span style={{ fontSize: '2.2rem', marginBottom: 8 }}>{cat.icon}</span>
                          <span style={{ fontWeight: 700, fontSize: '0.98rem', color: '#23272b', marginTop: 2 }}>{cat.name}</span>
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default CategoryPage; 